import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Autocomplete } from '@material-ui/lab';
import {
  Button,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { Search } from '@material-ui/icons';
import { localizationTable, options } from 'constants/table';
import { EnrollmentData, ReportCompanyData } from 'services/dataService';
import { toast } from 'react-toastify';
import MenuDownloadReport from 'components/MenuDownloadReport';
import { useStyles } from '../RecyclingTable/styles';

const STATUS_FILTERS = {
  all: '',
  expired: 'expired',
  onTime: 'onTime',
  dueSoon: 'dueSoon',
  blocked: 'blocked',
};

const buildCardConfig = summary => [
  {
    key: 'expired',
    title: 'Vencidas',
    value: summary.expired,
    total: summary.total,
    label: 'Matrículas vencidas',
    color: '#E74C3C',
    background: '#FDEDEC',
    icon: <WarningOutlinedIcon fontSize="small" />,
  },
  {
    key: 'onTime',
    title: 'Em dia',
    value: summary.onTime,
    total: summary.total,
    label: 'Matrículas em dia',
    color: '#2EB67D',
    background: '#EAF7F2',
    icon: <CheckCircleOutlineOutlinedIcon fontSize="small" />,
  },
  {
    key: 'dueSoon',
    title: 'Vencendo',
    value: summary.dueSoon,
    total: summary.total,
    label: 'Vencendo em até 30 dias',
    color: '#F39C12',
    background: '#FEF5E7',
    icon: <ScheduleOutlinedIcon fontSize="small" />,
  },
  {
    key: 'blocked',
    title: 'Bloqueadas',
    value: summary.blocked,
    total: summary.total,
    label: 'Matrículas bloqueadas',
    color: '#3F61E8',
    background: '#EAF2FF',
    icon: <LockOutlinedIcon fontSize="small" />,
  },
];

export default () => {
  const classes = useStyles();
  const tableRef = useRef();
  const filterRef = useRef('');
  const { t } = useTranslation(['button']);
  const { register, handleSubmit } = useForm();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS.all);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [isUpdatingExpiration, setIsUpdatingExpiration] = useState(false);
  const [summary, setSummary] = useState({
    total: 0,
    expired: 0,
    dueSoon: 0,
    onTime: 0,
    blocked: 0,
  });

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await EnrollmentData.getCompanyCourseCombo();
        const normalizedCourses = (response?.data?.courses || []).map(course => ({
          id: course?.id,
          title: course?.course?.name || course?.name || '',
        }));
        setCourses(normalizedCourses);
      } catch (error) {
        toast.error('Não foi possível carregar os cursos para o filtro.');
      }
    };

    loadCourses();
  }, []);

  const cards = useMemo(() => buildCardConfig(summary), [summary]);

  const handleSearch = data => {
    const nextFilter = data.filter || '';
    setFilter(nextFilter);
    filterRef.current = nextFilter;

    if (tableRef.current) {
      tableRef.current.state.query.page = 0;
      tableRef.current.onQueryChange();
    }
  };

  const handleFetch = async () => {
    try {
      const response = await ReportCompanyData.getAcademicEnrollmentExpiration({
        page: 1,
        limit: 99999,
        filter: filterRef.current,
        id_company_course: selectedCourse?.id,
        expiration_status: statusFilter,
      });

      return {
        dataToExport: response?.data?.academicEnrollmentExpiration || [],
      };
    } catch (error) {
      toast.error('Não foi possível baixar o relatório de expiração de matrículas.');
      return {
        dataToExport: [],
      };
    }
  };

  const getStatusChip = status => {
    if (status === 'Vencido') {
      return <Chip label={status} size="small" style={{ backgroundColor: '#FDEDEC', color: '#E74C3C' }} className={classes.statusChip} />;
    }

    if (status === 'Vence em até 30 dias') {
      return <Chip label={status} size="small" style={{ backgroundColor: '#FEF5E7', color: '#F39C12' }} className={classes.statusChip} />;
    }

    return <Chip label={status} size="small" style={{ backgroundColor: '#EAF7F2', color: '#2EB67D' }} className={classes.statusChip} />;
  };

  const getActiveChip = activeLabel => {
    const isBlocked = activeLabel === 'Bloqueada';

    return (
      <Chip
        label={activeLabel}
        size="small"
        style={{
          backgroundColor: isBlocked ? '#EAF2FF' : '#EAF7F2',
          color: isBlocked ? '#3F61E8' : '#2EB67D',
        }}
        className={classes.statusChip}
      />
    );
  };

  const handleOpenUpdateExpiration = rowData => {
    setSelectedEnrollment(rowData);
    setExpirationDate(rowData.due_date_value || '');
  };

  const handleCloseUpdateExpiration = () => {
    setSelectedEnrollment(null);
    setExpirationDate('');
  };

  const handleUpdateExpiration = async () => {
    if (!expirationDate) {
      toast.error('Informe uma nova data de expiração.');
      return;
    }

    try {
      setIsUpdatingExpiration(true);
      await ReportCompanyData.updateAcademicEnrollmentExpiration(
        selectedEnrollment.id_enrollment,
        { due_date: expirationDate }
      );
      toast.success('Expiração da matrícula atualizada com sucesso.');
      handleCloseUpdateExpiration();

      if (tableRef.current) {
        tableRef.current.onQueryChange();
      }
    } catch (error) {
      toast.error('Não foi possível atualizar a expiração da matrícula.');
    } finally {
      setIsUpdatingExpiration(false);
    }
  };

  return (
    <div className="notranslate">
      <Dialog
        fullWidth
        maxWidth="sm"
        open={!!selectedEnrollment}
        onClose={handleCloseUpdateExpiration}
      >
        <DialogTitle>
          Atualizar expiração da matrícula
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" style={{ marginBottom: 12 }}>
            {selectedEnrollment?.student_name} - {selectedEnrollment?.course_name}
          </Typography>
          <TextField
            fullWidth
            label="Nova data de expiração"
            type="date"
            variant="outlined"
            value={expirationDate}
            onChange={event => setExpirationDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" style={{ display: 'block', marginTop: 8 }}>
            Ao salvar, a matrícula será reativada caso esteja bloqueada.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={handleCloseUpdateExpiration}
            disabled={isUpdatingExpiration}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleUpdateExpiration}
            disabled={isUpdatingExpiration}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      <MaterialTable
        tableRef={tableRef}
        title={
          <div style={{ marginTop: 15, marginBottom: 15 }}>
            <Typography variant="h6">
              Relatório de Expiração de Matrículas
            </Typography>
            <Typography variant="subtitle2" style={{ fontSize: 12 }}>
              Acompanhe matrículas com data de expiração por curso, aluno, trilha e status de bloqueio.
            </Typography>
            <Typography variant="subtitle2" style={{ fontSize: 12 }}>
              Após o vencimento, o curso é bloqueado para o aluno alterando a matrícula para inativa.
            </Typography>
          </div>
        }
        columns={[
          { title: 'Curso', field: 'course_name', sorting: false },
          { title: 'Usuário', field: 'student_name', sorting: false },
          { title: 'E-mail', field: 'student_email', sorting: false },
          { title: 'CPF', field: 'student_cpf', sorting: false },
          { title: 'Data matrícula', field: 'enroll_created_at', sorting: false },
          { title: 'Vencimento', field: 'due_date', sorting: false },
          { title: 'Origem', field: 'assignment_type', sorting: false },
          { title: 'Trilha', field: 'trail_name', sorting: false },
          {
            title: 'Status',
            field: 'status_label',
            sorting: false,
            render: rowData => getStatusChip(rowData.status_label),
          },
          {
            title: 'Matrícula',
            field: 'active_label',
            sorting: false,
            render: rowData => getActiveChip(rowData.active_label),
          },
          {
            title: 'Ações',
            field: 'actions',
            sorting: false,
            render: rowData => (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleOpenUpdateExpiration(rowData)}
                className={classes.actionButton}
              >
                Atualizar prazo
              </Button>
            ),
          },
        ]}
        data={async query => {
          try {
            const response = await ReportCompanyData.getAcademicEnrollmentExpiration({
              page: query.page + 1,
              limit: query.pageSize,
              filter: filterRef.current,
              id_company_course: selectedCourse?.id,
              expiration_status: statusFilter,
            });

            const {
              academicEnrollmentExpiration,
              currentPage,
              totalCount,
              summary: responseSummary,
            } = response.data;

            setSummary(responseSummary || {
              total: 0,
              expired: 0,
              dueSoon: 0,
              onTime: 0,
              blocked: 0,
            });

            return {
              data: academicEnrollmentExpiration || [],
              page: (currentPage || 1) - 1,
              totalCount: totalCount || 0,
            };
          } catch (error) {
            setSummary({
              total: 0,
              expired: 0,
              dueSoon: 0,
              onTime: 0,
              blocked: 0,
            });
            toast.error('Não foi possível carregar o relatório de expiração de matrículas.');
            return {
              data: [],
              page: 0,
              totalCount: 0,
            };
          }
        }}
        components={{
          Toolbar: props => (
            <div style={{ position: 'relative' }}>
              <MTableToolbar {...props} />
              <CardContent style={{ marginBottom: 12, paddingBottom: 0 }}>
                <Grid container spacing={2}>
                  {cards.map(card => {
                    const percent = card.total ? Math.round((card.value / card.total) * 100) : 0;

                    return (
                      <Grid item xs={12} md={3} key={card.key}>
                        <Paper variant="outlined" className={classes.summaryCard}>
                          <Grid container justify="space-between" alignItems="center">
                            <Typography variant="subtitle2" className={classes.summaryTitle}>
                              {card.title}
                            </Typography>
                            <div
                              className={classes.summaryIcon}
                              style={{ backgroundColor: card.background, color: card.color }}
                            >
                              {card.icon}
                            </div>
                          </Grid>

                          <div className={classes.summaryValueRow}>
                            <Typography variant="h4" className={classes.summaryValue} style={{ color: card.color }}>
                              {card.value}
                            </Typography>
                            <Typography variant="h6" className={classes.summaryTotal}>
                              / {card.total}
                            </Typography>
                          </div>

                          <Typography variant="body2" className={classes.summaryLabel}>
                            {card.label}
                          </Typography>

                          <div className={classes.progressTrack}>
                            <div
                              className={classes.progressBar}
                              style={{
                                width: `${percent}%`,
                                backgroundColor: card.color,
                              }}
                            />
                          </div>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
              <Divider />
              <form onSubmit={handleSubmit(handleSearch)}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item md={4} xs={12}>
                      <TextField
                        fullWidth
                        label="Pesquisar"
                        name="filter"
                        type="text"
                        variant="outlined"
                        defaultValue={filter}
                        inputRef={register}
                      />
                    </Grid>
                    <Grid item md={4} xs={12}>
                      <Autocomplete
                        options={courses}
                        getOptionLabel={option => option?.title || ''}
                        value={selectedCourse}
                        onChange={(_, value) => {
                          setSelectedCourse(value);
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Curso"
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="expiration-status-filter">
                          Status
                        </InputLabel>
                        <Select
                          labelId="expiration-status-filter"
                          label="Status"
                          value={statusFilter}
                          onChange={event => setStatusFilter(event.target.value)}
                        >
                          <MenuItem value={STATUS_FILTERS.all}>Todos status</MenuItem>
                          <MenuItem value={STATUS_FILTERS.expired}>Vencido</MenuItem>
                          <MenuItem value={STATUS_FILTERS.onTime}>Em dia</MenuItem>
                          <MenuItem value={STATUS_FILTERS.dueSoon}>Vencendo em 30 dias</MenuItem>
                          <MenuItem value={STATUS_FILTERS.blocked}>Bloqueada</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item md={2} xs={12} className={classes.filterActions}>
                      <Button
                        color="primary"
                        variant="contained"
                        size="large"
                        type="submit"
                      >
                        <Search className={classes.icon} />
                        Buscar
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </form>
            </div>
          ),
        }}
        localization={localizationTable(t)}
        actions={[
          {
            icon: () => (
              <MenuDownloadReport
                handleFetch={handleFetch}
                tableRef={tableRef}
                fileName="RelatorioExpiracaoMatriculas"
                hasCSV
              />
            ),
            isFreeAction: true,
          },
        ]}
        options={{
          ...options,
          exportButton: false,
          doubleHorizontalScroll: 'true',
        }}
      />
    </div>
  );
};
